import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Switch, Button, Alert, CircularProgress, Snackbar
} from '@mui/material';
import api, { handleApiError } from '../../services/api';

export default function BusinessTypesContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [businessTypes, setBusinessTypes] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      console.log('🔄 [BusinessTypes] Fetching configuration from server...');
      const response = await api.get('/config/bootstrap');
      console.log('📥 [BusinessTypes] Received response:', response.data);
      
      if (response.data.success && response.data.config.enabledBusinessTypes) {
        const types = response.data.config.enabledBusinessTypes;
        console.log(`✅ [BusinessTypes] Loaded ${types.length} business types`);
        types.forEach(bt => console.log(`   - ${bt.type}: enabled=${bt.enabled}`));
        setBusinessTypes(types);
        setHasChanges(false);
      }
    } catch (error) {
      console.error('❌ [BusinessTypes] Failed to load:', error);
      setSnackbar({ open: true, message: handleApiError(error), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const toggleBusinessType = (index) => {
    setBusinessTypes(prev => prev.map((bt, i) => i === index ? { ...bt, enabled: !bt.enabled } : bt));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Log what we're about to save
      console.log('💾 [BusinessTypes] Saving changes...');
      console.log('💾 [BusinessTypes] Data to save:', businessTypes.map(bt => `${bt.type}:${bt.enabled}`).join(', '));
      
      // Clean the data before sending (remove MongoDB _id fields that might cause issues)
      const cleanedBusinessTypes = businessTypes.map(bt => ({
        type: bt.type,
        enabled: bt.enabled,
        displayName: bt.displayName,
        icon: bt.icon,
        description: bt.description
      }));
      
      console.log('💾 [BusinessTypes] Sending POST to /config/business-types...');
      const response = await api.post('/config/business-types', { 
        businessTypes: cleanedBusinessTypes, 
        updatedBy: 'admin' 
      });
      
      console.log('📥 [BusinessTypes] Save response:', response.data);
      
      if (response.data.success) {
        const enabledCount = response.data.businessTypes?.filter(bt => bt.enabled).length || 0;
        const message = `Business types updated! ${enabledCount} of ${response.data.businessTypes?.length || 0} enabled.`;
        console.log(`✅ [BusinessTypes] ${message}`);
        setSnackbar({ open: true, message, severity: 'success' });
        setHasChanges(false);
        
        // Update local state with server response to ensure sync
        if (response.data.businessTypes) {
          setBusinessTypes(response.data.businessTypes);
        }
      } else {
        console.error('❌ [BusinessTypes] Server returned success:false');
        setSnackbar({ open: true, message: 'Server returned an error', severity: 'error' });
      }
    } catch (error) {
      console.error('❌ [BusinessTypes] Save failed:', error);
      console.error('❌ [BusinessTypes] Error details:', error.response?.data || error.message);
      setSnackbar({ open: true, message: handleApiError(error), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>;

  const enabledCount = businessTypes.filter(bt => bt.enabled).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Control which business types are available during iOS app initial setup.
        </Typography>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={fetchConfig}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Changes take effect immediately for new installations. Existing users are not affected.
      </Alert>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ You have unsaved changes. Click "Save Business Types" to apply them.
        </Alert>
      )}

      <Grid container spacing={2}>
        {businessTypes.map((businessType, index) => (
          <Grid item xs={12} sm={6} md={4} key={businessType.type}>
            <Paper
              variant="outlined"
              sx={{ 
                p: 2, 
                opacity: businessType.enabled ? 1 : 0.5, 
                transition: 'all 0.3s',
                borderColor: businessType.enabled ? 'primary.main' : 'divider',
                borderWidth: businessType.enabled ? 2 : 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4">{businessType.icon}</Typography>
                  <Box>
                    <Typography variant="h6">{businessType.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {businessType.description}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={businessType.enabled}
                  onChange={() => toggleBusinessType(index)}
                  color="primary"
                />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {businessTypes.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No business types configured. Default types will be created when you save.
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {enabledCount} of {businessTypes.length} business types enabled
          {enabledCount === 0 && ' (at least 1 recommended)'}
        </Typography>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={saving || !hasChanges}
          color={hasChanges ? 'warning' : 'primary'}
        >
          {saving ? <CircularProgress size={24} /> : hasChanges ? 'Save Changes' : 'Saved ✓'}
        </Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

